import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import Image from '../../components/AppImage';
import Button from '../../components/ui/Button';
import { useWebsiteData } from '../../context/WebsiteDataContext';

const BlogPage = () => {
    const { getPublishedBlogs } = useWebsiteData();
    const blogs = getPublishedBlogs();
    const [selectedCategory, setSelectedCategory] = useState('all');

    // Extract unique categories
    const categories = ['all', ...new Set(blogs.map(blog => blog.category).filter(Boolean))];

    const filteredBlogs = selectedCategory === 'all'
        ? blogs
        : blogs.filter(blog => blog.category === selectedCategory);

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <div className="min-h-screen bg-background">
            <Header />

            {/* Hero Section */}
            <section className="pt-32 pb-16 bg-muted/30">
                <div className="container mx-auto px-4 text-center">
                    <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
                        Insights & Updates
                    </h1>
                    <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                        Expert perspectives on construction trends, regulatory compliance, and project management.
                    </p>
                </div>
            </section>

            {/* Main Content */}
            <section className="py-16">
                <div className="container mx-auto px-4">

                    {/* Category Filter */}
                    {categories.length > 1 && (
                        <div className="flex flex-wrap justify-center gap-2 mb-12">
                            {categories.map((category) => (
                                <button
                                    key={category}
                                    onClick={() => setSelectedCategory(category)}
                                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${selectedCategory === category
                                            ? 'bg-primary text-primary-foreground'
                                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                                        }`}
                                >
                                    {category.charAt(0).toUpperCase() + category.slice(1)}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Blog Grid */}
                    {blogs.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {filteredBlogs.map((blog) => (
                                <article key={blog.id} className="group bg-card border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                                    {/* Image */}
                                    <div className="relative h-48 overflow-hidden">
                                        <Image
                                            src={blog.featuredImage || '/assets/images/no_image.png'}
                                            alt={blog.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                        />
                                        <div className="absolute top-4 left-4">
                                            <span className="px-3 py-1 bg-primary/90 text-primary-foreground text-xs font-medium rounded-full backdrop-blur-sm">
                                                {blog.category}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="p-6">
                                        <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-3">
                                            <span className="flex items-center space-x-1">
                                                <Icon name="User" size={14} />
                                                <span>{blog.author}</span>
                                            </span>
                                            <span className="flex items-center space-x-1">
                                                <Icon name="Calendar" size={14} />
                                                <span>{formatDate(blog.publishDate || blog.createdAt)}</span>
                                            </span>
                                        </div>

                                        <h2 className="text-xl font-bold text-foreground mb-3 line-clamp-2 group-hover:text-primary transition-colors">
                                            {blog.title}
                                        </h2>

                                        <p className="text-muted-foreground mb-4 line-clamp-3">
                                            {blog.excerpt}
                                        </p>

                                        <div className="flex items-center justify-between pt-4 border-t border-border">
                                            <Link
                                                to={`/blog/${blog.id}`}
                                                className="text-primary font-medium hover:text-primary/80 transition-colors flex items-center space-x-1"
                                            >
                                                <span>Read More</span>
                                                <Icon name="ArrowRight" size={14} />
                                            </Link>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-20">
                            <Icon name="FileText" size={64} className="mx-auto text-muted-foreground/30 mb-4" />
                            <h3 className="text-xl font-semibold text-foreground mb-2">No articles found</h3>
                            <p className="text-muted-foreground">Check back soon for new content.</p>
                        </div>
                    )}
                </div>
            </section>

            {/* Newsletter / CTA */}
            <section className="py-16 bg-primary text-primary-foreground">
                <div className="container mx-auto px-4 text-center">
                    <h2 className="text-3xl font-bold mb-4">Stay Informed</h2>
                    <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
                        Subscribe to our newsletter for the latest updates on construction regulations and industry trends.
                    </p>
                    <div className="max-w-md mx-auto flex gap-2">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-4 py-2 rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                        />
                        <Button variant="secondary">Subscribe</Button>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default BlogPage;
